import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { CalendarIcon, Loader2, CheckCircle2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERVICES = ["Zaunbau", "Gartenarbeiten", "Erdarbeiten & Drainage", "Pflasterarbeiten"];

const inputClass =
  "w-full rounded-lg border border-[#E5E2DC] bg-white px-4 py-3 text-sm text-[#1A1C19] placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#244A32]/30 focus:border-[#244A32] transition";

export default function BookingForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "", website: "" });
  const [date, setDate] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.service) {
      toast.error("Bitte füllen Sie Name, E-Mail und Leistung aus.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/bookings`, {
        ...form,
        preferred_date: date ? format(date, "yyyy-MM-dd") : null,
      });
      setDone(true);
      toast.success("Anfrage gesendet! Wir melden uns zeitnah bei Ihnen.");
    } catch {
      toast.error("Senden fehlgeschlagen. Bitte versuchen Sie es erneut oder rufen Sie uns an: 0176 41162064");
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setForm({ name: "", email: "", phone: "", service: "", message: "", website: "" });
    setDate(null);
    setDone(false);
  };

  return (
    <section id="termin" className="py-16 sm:py-24 lg:py-32 px-4 sm:px-8 lg:px-16" data-testid="booking-section">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5 lg:sticky lg:top-24"
        >
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#244A32]">Termin & Anfrage</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-4">
            Buchen Sie Ihren Beratungstermin
          </h2>
          <p className="mt-4 text-base text-[#575953] leading-relaxed">
            Erzählen Sie uns kurz von Ihrem Vorhaben und wählen Sie einen Wunschtermin.
            Anatoli Zygankow meldet sich persönlich bei Ihnen – in der Regel innerhalb von 24 Stunden.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-[#575953]">
            <li className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-[#244A32] shrink-0" /> Kostenlose Erstberatung vor Ort
            </li>
            <li className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-[#244A32] shrink-0" /> Festpreis-Angebot ohne versteckte Kosten
            </li>
            <li className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-[#244A32] shrink-0" /> Direkter Draht zum Inhaber
            </li>
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="lg:col-span-7"
        >
          {done ? (
            <div className="bg-white rounded-2xl border border-[#E5E2DC] p-10 sm:p-14 text-center" data-testid="booking-success">
              <CheckCircle2 className="w-14 h-14 text-[#244A32] mx-auto" />
              <h3 className="font-heading text-xl sm:text-2xl font-semibold mt-6">Vielen Dank für Ihre Anfrage!</h3>
              <p className="mt-3 text-sm text-[#575953] max-w-md mx-auto leading-relaxed">
                Wir haben Ihre Buchung erhalten und Ihnen eine Bestätigung per E-Mail gesendet.
                Wir melden uns schnellstmöglich bei Ihnen. Bei dringenden Fragen erreichen Sie uns unter{" "}
                <a href="tel:+4917641162064" className="font-semibold text-[#244A32]" data-testid="success-phone-link">
                  0176 41162064
                </a>
                .
              </p>
              <button
                onClick={reset}
                data-testid="booking-new-request-button"
                className="mt-8 text-sm font-semibold text-[#244A32] underline underline-offset-4 hover:text-[#1D3B28]"
              >
                Weitere Anfrage stellen
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="bg-white rounded-2xl border border-[#E5E2DC] p-6 sm:p-10 space-y-6"
              data-testid="booking-form"
            >
              <div className="hidden" aria-hidden="true">
                <input type="text" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" />
              </div>

              <div>
                <label htmlFor="booking-service" className="block text-sm font-semibold mb-2">
                  Welche Leistung interessiert Sie? *
                </label>
                <div className="relative">
                  <select
                    id="booking-service"
                    data-testid="booking-service-select"
                    value={form.service}
                    onChange={set("service")}
                    required
                    className={`${inputClass} appearance-none pr-10`}
                  >
                    <option value="" disabled>
                      Bitte wählen …
                    </option>
                    {SERVICES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="w-4 h-4 absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Wunschtermin (optional)</label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="booking-date-trigger"
                      className={`${inputClass} flex items-center justify-between text-left ${!date ? "text-zinc-400" : ""}`}
                    >
                      {date ? format(date, "EEEE, d. MMMM yyyy", { locale: de }) : "Datum wählen …"}
                      <CalendarIcon className="w-4 h-4 text-zinc-400" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={(d) => {
                        setDate(d);
                        setCalendarOpen(false);
                      }}
                      disabled={{ before: new Date() }}
                      initialFocus
                      data-testid="booking-calendar"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="booking-name" className="block text-sm font-semibold mb-2">
                    Ihr Name *
                  </label>
                  <input
                    id="booking-name"
                    data-testid="booking-name-input"
                    type="text"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Max Mustermann"
                    required
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="booking-email" className="block text-sm font-semibold mb-2">
                    E-Mail-Adresse *
                  </label>
                  <input
                    id="booking-email"
                    data-testid="booking-email-input"
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="max@beispiel.de"
                    required
                    className={inputClass}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="booking-phone" className="block text-sm font-semibold mb-2">
                  Telefonnummer (optional)
                </label>
                <input
                  id="booking-phone"
                  data-testid="booking-phone-input"
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="0176 12345678"
                  className={inputClass}
                />
              </div>

              <div>
                <label htmlFor="booking-message" className="block text-sm font-semibold mb-2">
                  Ihre Nachricht
                </label>
                <textarea
                  id="booking-message"
                  data-testid="booking-message-input"
                  value={form.message}
                  onChange={set("message")}
                  rows={4}
                  placeholder="Beschreiben Sie kurz Ihr Vorhaben, z. B. ca. 20 m Zaun entlang der Grundstücksgrenze …"
                  className={`${inputClass} resize-none`}
                />
              </div>

              <button
                type="submit"
                data-testid="booking-submit-button"
                disabled={sending}
                className="w-full bg-[#244A32] hover:bg-[#1D3B28] disabled:opacity-60 text-white font-bold py-4 rounded-full transition-colors flex items-center justify-center gap-2"
              >
                {sending && <Loader2 className="w-4 h-4 animate-spin" />}
                {sending ? "Wird gesendet …" : "Anfrage senden"}
              </button>
              <p className="text-xs text-zinc-400 text-center">
                Mit dem Absenden stimmen Sie zu, dass wir Sie zu Ihrer Anfrage kontaktieren dürfen.
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
